import { Injectable } from '@angular/core';
import { IContractForm } from '@bp-core/src/lib/models/portal/goal/contract.form';
import { INewCustomCategoryFund } from '@bp-core/src/lib/models/portal/goal/new-custom-category-fund.model';
import { INewCustomFund } from '@bp-core/src/lib/models/portal/goal/new-custom-fund.model';
import {
  BaseGetRequest,
  BaseListRequest,
  GoalModel,
  InvestmentStrategyModel,
  PortfolioModel,
  RiskLevelModel,
  WhereGetRequest,
  WhereListRequest,
} from '@bp-core/src/lib/proto/common-message.pb';
import { CurrencyGrpcServiceClient } from '@bp-core/src/lib/proto/currency.pbsc';
import {
  ListFundingRequest,
  ListSubCategoryWithFundsByFinancialEntityRequest,
} from '@bp-core/src/lib/proto/funding.pb';
import { InvestmentStrategyGetRequest } from '@bp-core/src/lib/proto/investment-strategy.pb';
import { InvestmentStrategyGrpcServiceClient } from '@bp-core/src/lib/proto/investment-strategy.pbsc';
import {
  BaseGoalRequest,
  CreateGoalRequest,
  GetListPortfoliosWithRelationsRequest,
  UpdateByIdRequest,
} from '@bp-core/src/lib/proto/portal-goal.pb';
import { PortalGoalGrpcServiceClient } from '@bp-core/src/lib/proto/portal-goal.pbsc';
import { PortalUserGrpcServiceClient } from '@bp-core/src/lib/proto/portal-user.pbsc';
import { PortalGrpcServiceClient } from '@bp-core/src/lib/proto/portal.pbsc';
import {
  AddCustomPortfolioFundingComposition,
  AddCustomPortfolioRequest,
} from '@bp-core/src/lib/proto/portfolio.pb';
import { PortfolioGrpcServiceClient } from '@bp-core/src/lib/proto/portfolio.pbsc';
import { UserGrpcServiceClient } from '@bp-core/src/lib/proto/user.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { UserService } from '@bp-core/src/lib/services/portal/user.service';
import { Empty, StringValue } from '@ngx-grpc/well-known-types';
import { BpConfirmationService } from 'bp/shared/services/bp-confirmation.service';
import { GoalFundingService } from 'bp/shared/services/goal-funding.service';
import { UserStateService } from 'bp/shared/services/user-state.service';
import { FundingGrpcServiceClient } from 'projects/bp-core/src/lib/proto/funding.pbsc';
import {
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  combineLatest,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { CategoryCustomFundsService } from '../../../shared/services/category-custom-funds.service';
import { GoalNewDataInit } from '../models/goal-new-data.model';
import { ISubcategoryWithFundsResult } from '../models/subcategory-with-funds';

@Injectable({
  providedIn: 'root',
})
export class GoalDataService {
  private _unsubscribe$ = new Subject<void>();
  private _financialContractFormSubject = new ReplaySubject<IContractForm>(1);
  public readonly financialContractForm$ = this._financialContractFormSubject.asObservable();

  private _goalSubject = new ReplaySubject<GoalModel>(1);
  public readonly goal$ = this._goalSubject.asObservable();

  private _defaultInvestmentStrategySubject = new ReplaySubject<
    InvestmentStrategyModel | undefined
  >(1);
  public readonly defaultInvestmentStrategy$ =
    this._defaultInvestmentStrategySubject.asObservable();

  private _financialContractForm: IContractForm;

  constructor(
    private portalGrpcServiceClient: PortalGrpcServiceClient,
    private portalGoalGrpcServiceClient: PortalGoalGrpcServiceClient,
    private investmentStrategyGrpcServiceClient: InvestmentStrategyGrpcServiceClient,
    private portalUserGrpcServiceClient: PortalUserGrpcServiceClient,
    private goalFundingService: GoalFundingService,
    private fundingGrpcServiceClient: FundingGrpcServiceClient,
    private userGrpcServiceClient: UserGrpcServiceClient,
    private authService: AuthService,
    private userService: UserService,
    private bpConfirmationService: BpConfirmationService,
    private portfolioGrpcServiceClient: PortfolioGrpcServiceClient,
    private currencyGrpcServiceClient: CurrencyGrpcServiceClient,
    private categoryCustomFundsService: CategoryCustomFundsService,
    private userStateService: UserStateService,
  ) {
    this.financialContractForm$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(financialContractForm => {
        this._financialContractForm = financialContractForm;
      });
  }

  getCashFundingByEntityAndCurrency(financialEntityId: number, currencyId: number) {
    return this.fundingGrpcServiceClient
      .listFunding(
        new ListFundingRequest({
          where: `FundingFinancialEntities.Any(financialEntity.Id = ${financialEntityId}) and CurrencyId = ${currencyId} and IsBox = true`,
        }),
      )
      .pipe(take(1));
  }

  getSubcategoryWithCustomFunds(
    financialEntityId: number,
    portfolio?: PortfolioModel,
  ): Observable<ISubcategoryWithFundsResult> {
    return this.fundingGrpcServiceClient
      .listSubCategoryWithFundsByFinancialEntity(
        new ListSubCategoryWithFundsByFinancialEntityRequest({
          financialEntityId: financialEntityId,
          defaultCustomFunds: true,
        }),
      )
      .pipe(
        map(result => {
          const defaultCustomFunds = this.goalFundingService.getSubCategoryFundsWithCustomFunds(
            portfolio,
            result.categories,
          );
          this.categoryCustomFundsService.setCurrentCategoryCustomFunds(defaultCustomFunds);
          return {
            defaultCustomFunds: defaultCustomFunds,
            nonDefaultCustomFunds: [],
          };
        }),
      );
    return combineLatest([
      this.fundingGrpcServiceClient.listSubCategoryWithFundsByFinancialEntity(
        new ListSubCategoryWithFundsByFinancialEntityRequest({
          financialEntityId: financialEntityId,
          defaultCustomFunds: true,
        }),
      ),
      this.fundingGrpcServiceClient.listSubCategoryWithFundsByFinancialEntity(
        new ListSubCategoryWithFundsByFinancialEntityRequest({
          financialEntityId: financialEntityId,
          defaultCustomFunds: false,
        }),
      ),
    ]).pipe(
      map(([result1, result2]) => {
        const defaultCustomFunds = this.goalFundingService.getSubCategoryFundsWithCustomFunds(
          portfolio,
          result1.categories,
        );
        this.categoryCustomFundsService.setCurrentCategoryCustomFunds(defaultCustomFunds);
        return {
          defaultCustomFunds: defaultCustomFunds,
          nonDefaultCustomFunds: this.goalFundingService.getSubCategoryFundsWithCustomFunds(
            portfolio,
            result2.categories,
            0,
          ),
        };
      }),
    );
  }
  completeGoal(
    goal: GoalModel,
    userflowType: string,
    customCategoryFund?: INewCustomCategoryFund[],
    recommendedRiskLevelId?: number,
    options?: { sendEmailToggle: boolean; starredGoalToggle: boolean },
  ) {
    if (options) goal.starred = options.starredGoalToggle;

    const goalRequest = this.getGoalRequest(goal, recommendedRiskLevelId);
    const sendEmailToggle = options?.sendEmailToggle;

    if (userflowType === 'custom') {
      let fundsCustomArray: INewCustomFund[] = [];
      customCategoryFund?.forEach(value => {
        fundsCustomArray = [...fundsCustomArray, ...value.customFunds];
      });

      return this.createCustomGoal(goalRequest, sendEmailToggle ?? false, fundsCustomArray!, goal);
    } else {
      return this.createGoal(goalRequest, sendEmailToggle ?? false, goal);
    }
  }

  sendBrouchures(portfolioId: number) {
    return this.userService.sendBrochuresPortfolio(portfolioId).pipe(take(1));
  }

  getPortfolioById(portfolioId: number) {
    return this.portalGoalGrpcServiceClient
      .getPortfolio(
        new BaseGetRequest({
          include: 'PortfolioFunding.Funding,InvestmentStrategy,RiskLevel',
          where: new WhereGetRequest({
            predicate: new StringValue({ value: `id = ${portfolioId}` }),
          }),
        }),
      )
      .pipe(take(1));
  }
  getPortfolioByGoalId(goalId: number) {
    return this.portalGoalGrpcServiceClient
      .getPortfolioByGoalId(new BaseGoalRequest({ goalId: goalId }))
      .pipe(take(1));
  }
  getGoalById(goalId: number) {
    return combineLatest([
      this.portalGoalGrpcServiceClient.getMe(
        new BaseGetRequest({
          where: new WhereGetRequest({ predicate: new StringValue({ value: `id = ${goalId}` }) }),
        }),
      ),
      this.portalGoalGrpcServiceClient.getPortfolioByGoalId(
        new BaseGoalRequest({ goalId: goalId }),
      ),
    ]).pipe(
      map(([goal, portfolio]) => {
        goal.portfolio = portfolio;
        return goal;
      }),
    );
  }

  getPortfoliosByIds(portfolioIds: number[], simple: boolean = false) {
    return this.portalGoalGrpcServiceClient.getListPortfoliosWithRelations(
      new GetListPortfoliosWithRelationsRequest({
        simple: simple,
        baseListRequest: new BaseListRequest({
          where: new WhereListRequest({
            predicate: new StringValue({
              value: `Id IN (${portfolioIds.join(',')})`,
            }),
          }),
        }),
      }),
    );
  }
  getRiskLevelByEntityCustom(
    riskLevels: RiskLevelModel[],
    versionRiskLevel: string,
    stockPercentage: number,
  ) {
    if (riskLevels) {
      let riskLevelsFiltered = riskLevels.filter(risk => risk.version == versionRiskLevel);
      if (stockPercentage == 0) return riskLevelsFiltered.find(risk => risk.position == 1);
      if (this.between(stockPercentage, 0, 25))
        return riskLevelsFiltered.find(risk => risk.position == 2);
      if (this.between(stockPercentage, 25, 50))
        return riskLevelsFiltered.find(risk => risk.position == 3);
      if (this.between(stockPercentage, 50, 75))
        return riskLevelsFiltered.find(risk => risk.position == 4);
      if (this.between(stockPercentage, 75, 100))
        return riskLevelsFiltered.find(risk => risk.position == 5);
    }
    return undefined;
  }

  getPortfoliosByFinancialEntityId(financialEntityId: number, simple: boolean = false) {
    return this.portalGoalGrpcServiceClient
      .getListPortfoliosWithRelations(
        new GetListPortfoliosWithRelationsRequest({
          simple: simple,
          baseListRequest: new BaseListRequest({
            where: new WhereListRequest({
              predicate: new StringValue({
                value: `FinancialEntityId = ${financialEntityId} AND InvestmentStrategy.isVisible = true AND UserId = null`,
              }),
            }),
            include: 'PortfolioFunding.Funding,InvestmentStrategy,RiskLevel',
          }),
        }),
      )
      .pipe(take(1));
  }

  getActivationAccountData(category: string) {
    const currencyRequest = new BaseGetRequest({
      where: new WhereGetRequest({
        predicate: new StringValue({ value: 'uuid == "dolar"' }),
      }),
    });

    const categoryToStrategy: Record<string, string> = {
      'short-term-invest-dollars': 'ahorro-d-lar-2-0',
      'short-term-invest': 'ahorro-corto-plazo',
      'short-term-invest-plus': 'ahorro-corto-plazo-plus',
      'short-term-invest-dollars-plus': 'd-lar-plus-2-0',
    };
    return combineLatest([
      this.portalGrpcServiceClient.getGoalCategory(
        new BaseGetRequest({
          where: new WhereGetRequest({
            predicate: new StringValue({
              value: `Code = "${category}"`,
            }),
          }),
        }),
      ),
      this.portalGoalGrpcServiceClient.getPortfolio(
        new BaseGetRequest({
          include: 'PortfolioFunding.Funding,InvestmentStrategy,RiskLevel',
          where: new WhereGetRequest({
            predicate: new StringValue({
              value: `isDefault = true and InvestmentStrategy.code = "${categoryToStrategy[category]}"`,
            }),
          }),
        }),
      ),
      this.currencyGrpcServiceClient.get(currencyRequest),
    ])
      .pipe(take(1))
      .pipe(
        map(([category, portfolio, currencyUSD]) => {
          return {
            category: category,
            portfolio: portfolio,
            currencyUSD: currencyUSD,
          };
        }),
      );
  }

  cleanUp() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._goalSubject.complete();
  }
  private createGoal(goal: CreateGoalRequest, sendEmailToggle: boolean, goalToUpdate?: GoalModel) {
    // Flujo invest

    if (goalToUpdate?.id) {
      const updateGoalRequest = {
        ...goal.toJSON(),
        id: goalToUpdate.id,
        investmentStrategyId: goalToUpdate.portfolio?.investmentStrategyId,
      };

      return this.portalGoalGrpcServiceClient.updateById(new UpdateByIdRequest(updateGoalRequest));
    }
    return this.portalGoalGrpcServiceClient.createGoal(goal).pipe(
      switchMap(result => {
        const sendBrouchures$ = sendEmailToggle
          ? this.sendBrouchures(goal.portfolioId).pipe(
              catchError(error => {
                console.error('Error sending brochures:', error);
                return this.bpConfirmationService
                  .open(
                    {
                      title: 'Error',
                      message: 'No se pudieron enviar los folletos',
                    },
                    'error',
                  )
                  .afterClosed()
                  .pipe(map(() => null));
              }),
            )
          : of(null);
        return forkJoin([sendBrouchures$, this.authService.refreshUser()]).pipe(map(() => result));
      }),
    );
  }
  initializeData(goalId?: number): Observable<GoalNewDataInit> {
    const goalCategoriesRequest = this.portalGrpcServiceClient.getListGoalCategories(
      new BaseListRequest({
        include: 'GoalCategoryConfigCurrencies',
      }),
    );
    const portfolioRequest = this.portalGoalGrpcServiceClient.getListPortfoliosWithRelations(
      new GetListPortfoliosWithRelationsRequest({
        simple: true,
        baseListRequest: new BaseListRequest({
          include: 'PortfolioFunding.Funding,InvestmentStrategy,RiskLevel',
          where: new WhereListRequest({
            predicate: new StringValue({
              value: 'InvestmentStrategy.isVisible = true AND UserId = null',
            }),
          }),
        }),
      }),
    );

    const riskLevelRequest = this.portalGoalGrpcServiceClient.getAllRiskLevel(new Empty());
    const riskLevelCalculations = this.portalGoalGrpcServiceClient.getAllGoalRiskLevelCalculation(
      new BaseListRequest({ include: 'RiskLevel' }),
    );
    const meFinancialEntitiesRequest = this.portalUserGrpcServiceClient.getMeFinancialEntities(
      new Empty(),
    );
    const investmentsStrategiesRequest = this.investmentStrategyGrpcServiceClient.get(
      new InvestmentStrategyGetRequest({
        where: {
          predicate: new StringValue({ value: 'isVisible = true' }),

          order: new StringValue({ value: 'isRecommended desc' }),
        },
      }),
    );
    const investmentStrategyTypesRequest = this.investmentStrategyGrpcServiceClient.getType(
      new InvestmentStrategyGetRequest({
        where: { order: new StringValue({ value: 'isDefault desc, displayOrder asc' }) },
      }),
    );
    const investmentTypeRequest = this.investmentStrategyGrpcServiceClient.getInvestmentType(
      new InvestmentStrategyGetRequest({
        include: 'InvestmentStrategy',
      }),
    );
    const timeHorizonRequest = this.portalGoalGrpcServiceClient.getAllTimeHorizon(new Empty());

    const currentUserInformationRequest = this.userGrpcServiceClient.getMeInformation(new Empty());

    const currentUserObservable = this.authService.getCurrentUser();

    let goalRequest: Observable<GoalModel | undefined> = of(undefined);
    let portafolioRequest: Observable<PortfolioModel | undefined> = of(undefined);
    if (goalId) {
      goalRequest = this.portalGoalGrpcServiceClient.getMe(
        new BaseGetRequest({
          where: new WhereGetRequest({ predicate: new StringValue({ value: `id = ${goalId}` }) }),
        }),
      );
      portafolioRequest = this.portalGoalGrpcServiceClient.getPortfolioByGoalId(
        new BaseGoalRequest({ goalId: goalId }),
      );
    }

    return combineLatest([
      goalCategoriesRequest,
      portfolioRequest,
      riskLevelRequest,
      riskLevelCalculations,
      meFinancialEntitiesRequest,
      investmentsStrategiesRequest,
      investmentStrategyTypesRequest,
      investmentTypeRequest,
      timeHorizonRequest,
      currentUserInformationRequest,
      currentUserObservable,
      goalRequest,
      portafolioRequest,
      this.userStateService.currentStep$,
    ]).pipe(
      take(1),
      takeUntil(this._unsubscribe$),
      map(
        ([
          goalCategories,
          portfolios,
          riskLevels,
          riskLevelCalculations,
          meFinancialEntities,
          investmentStrategies,
          investmentStrategyTypes,
          investmentTypes,
          timeHorizons,
          currentUserInformation,
          currentUser,
          goal,
          portfolio,
          currentStep,
        ]) => {
          riskLevels.values = riskLevels.values
            ?.filter(r => r.code != 'custom')
            .sort((a, b) => a.position - b.position);
          meFinancialEntities?.values!.sort((a, b) => {
            if (
              a.userFinancialEntity?.financialEntity?.title! >
              b.userFinancialEntity?.financialEntity?.title!
            ) {
              return -1;
            }
            if (
              a.userFinancialEntity?.financialEntity?.title! <
              b.userFinancialEntity?.financialEntity?.title!
            ) {
              return 1;
            }
            return 0;
          });
          investmentStrategyTypes.values = investmentStrategyTypes.values?.filter(f => f.isVisible);
          if (goal && portfolio) {
            goal.portfolio = portfolio;
          }

          return {
            goalCategories: goalCategories.values,
            portfolios: portfolios.values,
            riskLevels: riskLevels.values,
            riskLevelCalculations: riskLevelCalculations.values,
            meFinancialEntities: meFinancialEntities.values,
            investmentStrategies: investmentStrategies.values,
            investmentStrategyTypes: investmentStrategyTypes.values,
            investmentTypes: investmentTypes.values,
            timeHorizons: timeHorizons.values,
            currentUserInformation: currentUserInformation,
            currentUser: currentUser,
            goal: goal,
            canShowSemiLiquidFlow: currentStep.extraConditions?.showSemiLiquidFlow ?? false,
          };
        },
      ),
    );
  }
  private createCustomGoal(
    goal: CreateGoalRequest,
    sendEmailToggle: boolean,
    fundsCustomArray: INewCustomFund[],
    goalToUpdate?: GoalModel,
  ) {
    const request = new AddCustomPortfolioRequest();
    fundsCustomArray = fundsCustomArray?.filter(fund => fund.userPercentage! > 0);
    fundsCustomArray.forEach(fund => {
      const custom = new AddCustomPortfolioFundingComposition();
      custom.fundingId = fund.fund.fund?.id!;
      custom.percentage = fund.userPercentage!;
      request.funds?.push(custom);
    });
    request.riskLevelId = goal.riskLevelId;
    request.financialEntityId = goal.financialEntityId;
    return this.portfolioGrpcServiceClient.addCustomPortfolio(request).pipe(
      take(1),
      switchMap(result => {
        goal.portfolioId = result.portfolio?.id!;
        goal.riskLevelId = result.portfolio?.riskLevelId!;

        // Una vez que addCustomPortfolio se completa con éxito, llamamos a addGoal
        return this.createGoal(goal, sendEmailToggle, goalToUpdate);
      }),
    );
  }
  private getGoalRequest(goal: GoalModel, recommendedRiskLevelId?: number) {
    const goalRequest = new CreateGoalRequest({
      currencyId: goal.currencyId?.value,
      displayCurrencyId: goal.displayCurrencyId?.value,
      goalCategoryId: goal.goalCategoryId,
      title: goal.title,
      portfolioId: goal.portfolioId?.value,
      riskLevelId: goal.riskLevelId?.value,
      financialEntityId: goal.financialEntityId?.value,
      icon: goal.icon,
      initialInvestment: goal.initialInvestment,
      monthlyContribution: goal.monthlyContribution,
      starred: goal.starred,
      targetAmount: Math.round(goal.targetAmount),
      years: goal.years,
      recommendedRiskLevelId: recommendedRiskLevelId ?? goal.portfolio?.riskLevelId,
      imageLarge: goal.imageLarge,
      imageSmall: goal.imageSmall,
    });
    return goalRequest;
  }
  private between(x: number, min: number, max: number): boolean {
    return x > min && x <= max;
  }
}
